import * as React from "react";
import clsx from "clsx";
import Button from "../Button/Button";
import Checkbox from "../../form/Checkbox/Checkbox";
import Select from "../../form/Select/Select";
import TextInput from "../../form/TextInput/TextInput";
import styles from "./DataTable.module.scss";

export const DataTableBaseButton = React.forwardRef<HTMLButtonElement, any>(
  ({ children, className, startIcon, disabled, onClick, title, "aria-label": ariaLabel, ...props }, ref) => (
    <Button
      ref={ref}
      size="sm"
      variant="outline"
      intent="secondary"
      label={ariaLabel ?? title ?? (typeof children === "string" ? children : "Action")}
      className={className}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {startIcon}
      {children}
    </Button>
  ),
);
DataTableBaseButton.displayName = "DataTableBaseButton";

export const DataTableBaseTextField = React.forwardRef<HTMLInputElement, any>(
  ({ className, inputRef, slotProps, label: _label, size: _size, fullWidth, value, onChange, ...props }, ref) => (
    <TextInput
      ref={inputRef}
      className={className}
      value={value}
      onChange={onChange}
      fullWidth={fullWidth}
      startAdornment={slotProps?.input?.startAdornment}
      endAdornment={slotProps?.input?.endAdornment}
      {...slotProps?.htmlInput}
      {...props}
    />
  ),
);
DataTableBaseTextField.displayName = "DataTableBaseTextField";

export const DataTableBaseCheckbox = React.forwardRef<HTMLButtonElement, any>(
  (
    {
      className,
      inputRef,
      checked,
      indeterminate,
      onChange,
      label,
      name,
      disabled,
      density: _density,
      fullWidth: _fullWidth,
      ...props
    },
    ref,
  ) => {
    const checkbox = (
      <Checkbox
        ref={(node) => {
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
          if (typeof inputRef === "function") inputRef(node);
          else if (inputRef) inputRef.current = node;
        }}
        checked={checked}
        indeterminate={indeterminate}
        disabled={disabled}
        size="sm"
        name={name}
        onCheckedChange={(nextChecked) => {
          onChange?.({
            target: { checked: nextChecked, name },
            currentTarget: { checked: nextChecked, name },
          });
        }}
        {...props}
      />
    );

    if (!label) return checkbox;

    return (
      <label className={clsx(styles.checkboxControl, className)}>
        {checkbox}
        <span>{label}</span>
      </label>
    );
  },
);
DataTableBaseCheckbox.displayName = "DataTableBaseCheckbox";

const optionsFromChildren = (children: React.ReactNode) =>
  React.Children.toArray(children).map((child: any) => ({
    value: String(child.props?.value ?? ""),
    label: String(child.props?.children ?? child.props?.value ?? ""),
    isDisabled: Boolean(child.props?.disabled),
  }));

export const DataTableBaseSelect = React.forwardRef<any, any>(
  (
    {
      children,
      className,
      value,
      onChange,
      disabled,
      fullWidth,
      label: _label,
      native: _native,
      size: _size,
      slotProps,
      ...props
    },
    ref,
  ) => (
    <Select
      ref={ref}
      className={className}
      value={value == null ? null : String(value)}
      disabled={disabled}
      fullWidth={fullWidth}
      searchable={false}
      options={optionsFromChildren(children)}
      onValueChange={(nextValue) => {
        onChange?.({
          target: { value: nextValue },
          currentTarget: { value: nextValue },
        });
      }}
      {...slotProps?.htmlInput}
      {...props}
    />
  ),
);
DataTableBaseSelect.displayName = "DataTableBaseSelect";

export const DataTableBaseSelectOption = ({ children }: any) => <>{children}</>;
